import { Component, EventEmitter, Output, ChangeDetectionStrategy, Input, AfterViewInit, OnDestroy, HostBinding, ChangeDetectorRef, ElementRef, Injector } from '@angular/core';
import { getPageName } from 'app/app-page-helpers';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { NavController } from 'providers/nav-controller';
import { PageRef } from 'providers/page-ref';
import { DestroyGuard } from 'services/browser-utils';
import { ScrollHideService, ScrollHideRegistration } from 'services/scroll-hide.service';

export interface BottomNavItem {
  icon: string;
  label: string;
  pageRef?: PageRef<any, any>;
  callback?: () => void;
  isActive?: boolean;
  isEnabled?: () => boolean;
}

// pages where the center button shows play icon and triggers start workout
const WORKOUT_START_PAGES = ['TrainingPage'];

// css variable holding the bottom nav's real rendered height. cs-page-action-buttons
// sits on top of the nav on root pages and reads it; a fixed guess was too small on
// iOS, where the nav also covers the home-indicator area, so the nav hid the bottom
// of the action bar
export const BOTTOM_NAV_HEIGHT_CSS_VAR = '--cs-bottom-nav-height';

@Component({
  selector: 'cs-bottom-nav',
  templateUrl: 'cs-bottom-nav.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'bottom-nav'
  }
})
export class CsBottomNavComponent implements AfterViewInit, OnDestroy {
  @Input() items: BottomNavItem[] = [];
  @Input() canStartWorkout = false;
  @Input() addButtonEnabled = true;
  @Input() visible = true;
  @Output() itemClick = new EventEmitter<BottomNavItem>();
  @Output() addClick = new EventEmitter<void>();
  @Output() startWorkoutClick = new EventEmitter<void>();

  // whether center button should show play icon and start workout
  // only when on a workout page AND the workout is startable (not past)
  get isWorkoutStartMode(): boolean {
    const activePageName = this.navCtrl.getActivePageName();
    return WORKOUT_START_PAGES.includes(activePageName) && this.canStartWorkout;
  }
  @HostBinding('class.visible') get isVisible() { return this.visible; }
  @HostBinding('style.transform') get transform(): string {
    // don't apply scroll-hide transform when hidden - CSS handles position
    if (!this.visible) return null;
    return `translateY(${this.translateY}px)`;
  }

  hasClientSmallScreen = hasClientSmallScreen;

  // scroll detection properties
  private translateY = 0;
  private scrollHideRegistration: ScrollHideRegistration;
  private destroyGuard = new DestroyGuard();
  private heightObserver: ResizeObserver | null = null;

  constructor(
    private elementRef: ElementRef,
    private cd: ChangeDetectorRef,
    private injector: Injector,
    private scrollHideService: ScrollHideService,
    private navCtrl: NavController
  ) {}

  async ngAfterViewInit() {
    // publish the nav's height for the floating action bar, and keep it current
    const navElement = this.elementRef.nativeElement as HTMLElement;
    this.heightObserver = new ResizeObserver(() => this.publishHeight(navElement));
    this.heightObserver.observe(navElement);
    this.publishHeight(navElement);

    // wait for Angular to complete updates before setting up scroll detection.
    // the wait now returns when this component is destroyed instead of hanging
    // (df-3f0eaff27ac6), so registering below has to be skipped in that case —
    // ngOnDestroy has already run by then, so nothing would ever unregister it
    if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;

    // register with ScrollHideService for scroll-based hiding
    // ScrollHideService will automatically handle refreshing on navigation
    this.scrollHideRegistration = this.scrollHideService.register({
      element: this.elementRef.nativeElement,
      direction: 'down', // bottom nav hides by translating down
      onScrollChange: (translateY) => {
        this.translateY = translateY;
        this.cd.markForCheck();
      },
      hideType: 'auto'
    });
  }

  ngOnDestroy() {
    this.destroyGuard.destroy();
    this.heightObserver?.disconnect();
    this.heightObserver = null;
    document.documentElement.style.removeProperty(BOTTOM_NAV_HEIGHT_CSS_VAR);
    // unregister from ScrollHideService
    if (this.scrollHideRegistration) {
      this.scrollHideRegistration.unregister();
    }
  }

  private publishHeight(navElement: HTMLElement) {
    // if the nav is not laid out (display: none), keep the last good value
    const height = navElement.offsetHeight;
    if (height > 0) {
      document.documentElement.style.setProperty(BOTTOM_NAV_HEIGHT_CSS_VAR, `${height}px`);
    }
  }

  handleItemClick(item: BottomNavItem) {
    // only handle click if item is enabled
    if (!this.isItemEnabled(item)) {
      return;
    }

    if (item.callback) {
      item.callback();
    } else {
      this.itemClick.emit(item);
    }
  }

  handleCenterButtonClick() {
    // if on a workout start page, emit startWorkoutClick
    if (this.isWorkoutStartMode) {
      this.startWorkoutClick.emit();
    } else {
      this.addClick.emit();
    }
  }

  isItemActive(item: BottomNavItem): boolean {
    const activePageName = this.navCtrl.getActivePageName();
    return item.pageRef && getPageName(item.pageRef) === activePageName;
  }

  isItemEnabled(item: BottomNavItem): boolean {
    return item.isEnabled == null || item.isEnabled();
  }
}